import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiFetch, ApiError, API_BASE } from '../lib/api';
import { DIMENSION_LABELS, type SurveyDimension } from '../config/surveyQuestions';

// Sprint ACAD ACT.A7 (#1708) — End-of-Programme Survey results, Faculty
// Supervisor / platform-admin-facing page.
//
// Q5/Q8 adjudication: this page never receives a student_id/name from the
// backend — GET /results for this role returns only the cohort aggregate
// plus an anonymized per-respondent breakdown ("Respondent N"). The PDF
// export button hits the same anonymized data via GET /results/pdf.

interface IndividualRow {
  respondent:  string;
  byDimension: Record<string, number>;
  overallMean: number | null;
}

interface ResultsResponse {
  launchId:   string | null;
  launchedAt: string | null;
  aggregate:  { byDimension: Record<string, number>; overallMean: number | null };
  individual: IndividualRow[];
}

const DIMENSIONS: SurveyDimension[] = [
  'PLATFORM_EXPERIENCE',
  'LEARNING_OUTCOMES',
  'CAREER_CONFIDENCE',
  'PLACEMENT_PREPAREDNESS',
];

function fmt(mean: number | null | undefined): string {
  if (mean === null || mean === undefined || Number.isNaN(mean)) return '—';
  return mean.toFixed(2);
}

export default function SurveyResultsPage() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const [results, setResults]   = useState<ResultsResponse | null>(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!cohortId) return;
    apiFetch<ResultsResponse>(`/api/academic/cohorts/${cohortId}/survey/results`)
      .then((res) => { setResults(res); setLoading(false); })
      .catch((e) => {
        setError(e instanceof ApiError ? e.message : 'Failed to load results');
        setLoading(false);
      });
  }, [cohortId]);

  async function handleExportPdf() {
    if (!cohortId) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/api/academic/cohorts/${cohortId}/survey/results/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey-summary-${cohortId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-6)',
  };

  return (
    <main style={{ flex: 1, padding: 'var(--space-8)', maxWidth: 1000, margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            End-of-Programme Survey — Results
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            Individual scores are anonymized — no student identity is shown or exported.
          </p>
        </div>
        {results?.launchId && (
          <button
            type="button"
            disabled={exporting}
            onClick={handleExportPdf}
            style={{
              padding: '10px 20px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
              background: 'var(--primary)', color: 'white', fontSize: 14, fontWeight: 600,
              cursor: exporting ? 'not-allowed' : 'pointer', flexShrink: 0,
            }}
          >
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Loading…</p>
      ) : error ? (
        <p style={{ color: 'var(--error)', fontFamily: 'var(--font-body)' }}>{error}</p>
      ) : !results?.launchId ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)' }}>
            No survey has been launched for this cohort yet.
          </p>
        </div>
      ) : (
        <>
          <div style={{ ...cardStyle, marginBottom: 'var(--space-6)' }}>
            <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: '0 0 var(--space-4)' }}>
              Dimension Breakdown
            </h2>
            {DIMENSIONS.map(dim => (
              <div key={dim} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: 'var(--text)', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                <span>{DIMENSION_LABELS[dim]}</span>
                <span style={{ fontWeight: 600 }}>{fmt(results.aggregate.byDimension[dim])}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 700, color: 'var(--text)', paddingTop: 12, marginTop: 8, borderTop: '2px solid var(--primary)' }}>
              <span>Overall Mean</span>
              <span>{fmt(results.aggregate.overallMean)}</span>
            </div>
          </div>

          <div style={cardStyle}>
            <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: '0 0 var(--space-4)' }}>
              Anonymous Individual Scores
            </h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase' }}>
                    <th style={{ padding: '6px 8px' }}>Respondent</th>
                    {DIMENSIONS.map(dim => (
                      <th key={dim} style={{ padding: '6px 8px' }}>{DIMENSION_LABELS[dim]}</th>
                    ))}
                    <th style={{ padding: '6px 8px' }}>Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {results.individual.map(row => (
                    <tr key={row.respondent} style={{ borderTop: '1px solid var(--border)', color: 'var(--text)' }}>
                      <td style={{ padding: '6px 8px' }}>{row.respondent}</td>
                      {DIMENSIONS.map(dim => (
                        <td key={dim} style={{ padding: '6px 8px' }}>{fmt(row.byDimension[dim])}</td>
                      ))}
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>{fmt(row.overallMean)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
